import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { useCalendar } from '../context/CalendarContext';
import { generateTimeSlots } from '../utils/timeHelpers';
import { detectOverlappingEvents } from '../utils/eventPositioning';
import MobileEventItem from './MobileEventItem';

const MOBILE_HOUR_HEIGHT = 80; // pixels per hour

const MobileDayView = ({
                           selectedDay,
                           events = [],
                           renderEvent,
                           onEventClick,
                           onSlotClick,
                           className = '',
                       }) => {
    const { timeRange, features, workingHours } = useCalendar();

    // Compute time range from working hours (handles both openingTime/closingTime and startTime/endTime)
    const { gridStartHour, gridEndHour } = useMemo(() => {
        // First try to get from timeRange context
        let startHour = typeof timeRange?.startHour === 'string'
            ? parseInt(timeRange.startHour.split(':')[0])
            : timeRange?.startHour;
        let endHour = typeof timeRange?.endHour === 'string'
            ? parseInt(timeRange.endHour.split(':')[0])
            : timeRange?.endHour;

        // If timeRange didn't give us good values, compute from workingHours directly
        if (!startHour || !endHour || startHour >= endHour) {
            if (workingHours && workingHours.length > 0) {
                let earliest = 24;
                let latest = 0;

                workingHours.forEach((wh) => {
                    // Handle both naming conventions
                    const openTime = wh.openingTime || wh.startTime;
                    const closeTime = wh.closingTime || wh.endTime;

                    if (openTime) {
                        const hour = parseInt(openTime.split(':')[0], 10);
                        earliest = Math.min(earliest, hour);
                    }
                    if (closeTime) {
                        const hour = parseInt(closeTime.split(':')[0], 10);
                        latest = Math.max(latest, hour + 1);
                    }
                });

                if (earliest < 24) startHour = earliest;
                if (latest > 0) endHour = Math.min(latest, 24);
            }
        }

        // Fallback to defaults
        return {
            gridStartHour: startHour || 6,
            gridEndHour: endHour || 22,
        };
    }, [timeRange, workingHours]);

    const timeSlots = generateTimeSlots(
        gridStartHour,
        gridEndHour,
        60
    );

    // Check if slot is within working hours for this day
    const isSlotWithinWorkingHours = useMemo(() => {
        if (!workingHours || workingHours.length === 0) {
            return () => true;
        }

        const dayOfWeekMapping = {
            1: 'MONDAY',
            2: 'TUESDAY',
            3: 'WEDNESDAY',
            4: 'THURSDAY',
            5: 'FRIDAY',
            6: 'SATURDAY',
            7: 'SUNDAY',
        };

        const dayOfWeek = dayOfWeekMapping[selectedDay.isoWeekday()];
        const dayHours = workingHours.find((wh) => wh.dayOfWeek === dayOfWeek);

        if (!dayHours) {
            return () => false;
        }

        const [openHour, openMin] = dayHours.openingTime.split(':').map(Number);
        const [closeHour, closeMin] = dayHours.closingTime.split(':').map(Number);
        const openMinutes = openHour * 60 + openMin;
        const closeMinutes = closeHour * 60 + closeMin;

        return (slotTime) => {
            const [slotHour, slotMin] = slotTime.split(':').map(Number);
            const slotMinutes = slotHour * 60 + (slotMin || 0);
            return slotMinutes >= openMinutes && slotMinutes < closeMinutes;
        };
    }, [workingHours, selectedDay]);

    const dayEvents = useMemo(() => {
        return events.filter((event) => {
            return dayjs(event.startTime).isSame(selectedDay, 'day');
        }).sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
    }, [events, selectedDay]);

    // Calculate overlap positions for all events
    const overlapPositions = useMemo(() => {
        return detectOverlappingEvents(dayEvents);
    }, [dayEvents]);

    // Calculate position and height for an event
    const getEventStyle = (event) => {
        const eventStart = dayjs(event.startTime);
        const eventEnd = dayjs(event.endTime);

        const startHour = eventStart.hour();
        const startMinute = eventStart.minute();
        const durationMinutes = eventEnd.diff(eventStart, 'minute');

        // Calculate top position (relative to gridStartHour)
        const hoursFromStart = (startHour - gridStartHour) + (startMinute / 60);
        const top = hoursFromStart * MOBILE_HOUR_HEIGHT;

        // Calculate height based on duration
        const height = (durationMinutes / 60) * MOBILE_HOUR_HEIGHT;

        // Get overlap info
        const eventKey = event.id || `${event.startTime}-${event.endTime}`;
        const overlap = overlapPositions.get(eventKey) || {
            width: '100%',
            left: '0',
            totalColumns: 1,
        };

        // Calculate actual left and width accounting for time label (70px)
        const eventWidth = overlap.totalColumns > 1
            ? `calc((100% - 78px) * ${1 / overlap.totalColumns})`
            : 'calc(100% - 78px)';
        const eventLeft = overlap.totalColumns > 1
            ? `calc(70px + (100% - 78px) * ${overlap.left.replace('%', '') / 100})`
            : '70px';

        return {
            position: 'absolute',
            top: `${Math.max(top, 0)}px`,
            height: `${Math.max(height, 30)}px`, // minimum height
            left: eventLeft,
            width: eventWidth,
            zIndex: 10,
        };
    };

    const formattedDate = selectedDay.format('dddd, D MMMM');

    const handleSlotClick = (slot) => {
        if (features.slotSelection && onSlotClick) {
            const isWithinHours = isSlotWithinWorkingHours(slot.time);
            if (isWithinHours) {
                onSlotClick({
                    day: selectedDay,
                    time: slot.time,
                    date: selectedDay,
                });
            }
        }
    };

    const defaultRenderEvent = (event) => {
        const style = getEventStyle(event);
        // Add small gap at bottom to separate from next event
        const wrapperStyle = {
            ...style,
            paddingBottom: '2px',
        };
        const innerStyle = {
            height: 'calc(100% - 2px)',
            boxSizing: 'border-box',
        };
        return (
            <div key={event.id || `${event.startTime}-${event.endTime}`} style={wrapperStyle}>
                <MobileEventItem
                    event={event}
                    onClick={() => onEventClick?.(event)}
                    fillHeight={true}
                    style={innerStyle}
                />
            </div>
        );
    };

    // Calculate total height of the timeline
    const totalHours = gridEndHour - gridStartHour;
    const timelineHeight = totalHours * MOBILE_HOUR_HEIGHT;

    return (
        <div className={`calendar-mobile__content ${className}`}>
            <div className="calendar-mobile__date-header">{formattedDate}</div>

            <div
                className="calendar-mobile__timeline"
                style={{ position: 'relative', height: `${timelineHeight}px` }}
            >
                {/* Time slot grid lines */}
                {timeSlots.map((slot, index) => {
                    const isWithinHours = isSlotWithinWorkingHours(slot.time);
                    return (
                        <div
                            key={slot.time}
                            className={`calendar-mobile__time-slot ${!isWithinHours ? 'calendar-mobile__time-slot--non-working' : ''}`}
                            style={{
                                position: 'absolute',
                                top: `${index * MOBILE_HOUR_HEIGHT}px`,
                                left: 0,
                                right: 0,
                                height: `${MOBILE_HOUR_HEIGHT}px`,
                                boxSizing: 'border-box',
                                cursor: isWithinHours && features.slotSelection ? 'pointer' : 'default',
                                backgroundColor: !isWithinHours ? '#f3f4f6' : undefined,
                            }}
                            onClick={() => handleSlotClick(slot)}
                        >
                            <div className="calendar-mobile__time-label">{slot.time}</div>
                        </div>
                    );
                })}

                {/* Events layer */}
                {dayEvents.map((event) => {
                    const style = getEventStyle(event);

                    if (renderEvent) {
                        // Wrap custom rendered event with positioning
                        return (
                            <div
                                key={event.id || `${event.startTime}-${event.endTime}`}
                                style={style}
                            >
                                {renderEvent(event)}
                            </div>
                        );
                    }

                    return defaultRenderEvent(event);
                })}
            </div>

            {/* Empty state message (only if no working hours for this day) */}
            {timeSlots.every(slot => !isSlotWithinWorkingHours(slot.time)) && (
                <div className="calendar-mobile__no-working-hours">
                    <div className="calendar-mobile__empty-text">Brak godzin pracy</div>
                    <div className="calendar-mobile__empty-subtext">
                        W tym dniu nie ma ustalonych godzin pracy.
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileDayView;